import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useFormik } from "formik";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import CloseIcon from "@mui/icons-material/Close";
import RouteIcon from "@mui/icons-material/AltRoute";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import SearchIcon from "@mui/icons-material/Search";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { useCreateRouteMutation } from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import { useStyles } from "../../../theme/appStyles";
import { useNavigate, useLocation } from "react-router";
import { IRoutes } from "../../../Api/Interface/api.interface";

// ── Types ─────────────────────────────────────────────────────────────────────
interface DaySchedule {
  enabled: boolean;
  startTime: string;
  endTime: string;
}

interface DeliverySchedule {
  [day: string]: DaySchedule;
}

// ── Constants ─────────────────────────────────────────────────────────────────
const DAYS = [
  { key: "Monday",    short: "Mon", weekend: false },
  { key: "Tuesday",   short: "Tue", weekend: false },
  { key: "Wednesday", short: "Wed", weekend: false },
  { key: "Thursday",  short: "Thu", weekend: false },
  { key: "Friday",    short: "Fri", weekend: false },
  { key: "Saturday",  short: "Sat", weekend: true  },
  { key: "Sunday",    short: "Sun", weekend: true  },
];

const TORONTO_CITIES = [
  "Ajax", "Annex", "Brampton", "Burlington", "Chinatown",
  "Distillery District", "Downtown", "East York", "Etobicoke",
  "Forest Hill", "Greektown", "Harbourfront", "High Park",
  "Kensington Market", "King West", "Leaside", "Liberty Village",
  "Little Italy", "Markham", "Mississauga", "North York", "Oakville",
  "Oshawa", "Pickering", "Queen West", "Richmond Hill", "Rosedale",
  "Scarborough", "The Beaches", "Thornhill", "Vaughan", "Whitby", "York",
];

const DEFAULT_SCHEDULE: DeliverySchedule = Object.fromEntries(
  DAYS.map((d) => [d.key, { enabled: false, startTime: "", endTime: "" }])
);

// ── Quarter-hour time picker ──────────────────────────────────────────────────
// Renders two selects: Hour (1-12 + AM/PM) and Minute (00/15/30/45)
// Stores value as "HH:MM" 24-hour string to stay compatible with backend
const HOURS = Array.from({ length: 12 }, (_, i) => i + 1);   // 1..12
const MINUTES = ["00", "15", "30", "45"];

function parseTime(val: string): { h: string; m: string; period: "AM" | "PM" } {
  if (!val) return { h: "", m: "", period: "AM" };
  const [hStr, mStr] = val.split(":");
  const h24 = parseInt(hStr, 10);
  const period: "AM" | "PM" = h24 >= 12 ? "PM" : "AM";
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return { h: String(h12), m: mStr || "00", period };
}

function buildTime(h: string, m: string, period: "AM" | "PM"): string {
  if (!h || !m) return "";
  let h24 = parseInt(h, 10);
  if (period === "AM" && h24 === 12) h24 = 0;
  if (period === "PM" && h24 !== 12) h24 += 12;
  return `${String(h24).padStart(2, "0")}:${m}`;
}

interface QuarterTimePickerProps {
  value: string;        // "HH:MM" 24-hr
  onChange: (v: string) => void;
  disabled?: boolean;
}

const QuarterTimePicker: React.FC<QuarterTimePickerProps> = ({ value, onChange, disabled }) => {
  const { h, m, period } = parseTime(value);

  const update = (newH: string, newM: string, newP: "AM" | "PM") => {
    onChange(buildTime(newH, newM, newP));
  };

  const selectSx = {
    fontSize: 12,
    "& .MuiSelect-select": { py: "5px", px: "6px" },
    "& .MuiOutlinedInput-notchedOutline": { borderRadius: "6px" },
  };

  return (
    <Box display="flex" gap={0.5} mt={1}>
      {/* Hour */}
      <Select
        size="small" disabled={disabled} value={h}
        onChange={(e) => update(e.target.value, m || "00", period)}
        displayEmpty
        sx={{ ...selectSx, flex: 1 }}
        renderValue={(v) => v || <span style={{ color: "#aaa" }}>HH</span>}
        MenuProps={{ PaperProps: { sx: { maxHeight: 200 } } }}
      >
        {HOURS.map((hr) => (
          <MenuItem key={hr} value={String(hr)} sx={{ fontSize: 12 }}>
            {String(hr).padStart(2, "0")}
          </MenuItem>
        ))}
      </Select>

      {/* Minute */}
      <Select
        size="small" disabled={disabled || !h} value={m || ""}
        onChange={(e) => update(h, e.target.value, period)}
        displayEmpty
        sx={{ ...selectSx, flex: 1 }}
        renderValue={(v) => v || <span style={{ color: "#aaa" }}>MM</span>}
      >
        {MINUTES.map((min) => (
          <MenuItem key={min} value={min} sx={{ fontSize: 12 }}>{min}</MenuItem>
        ))}
      </Select>

      {/* AM / PM */}
      <Select
        size="small" disabled={disabled || !h} value={period}
        onChange={(e) => update(h, m || "00", e.target.value as "AM" | "PM")}
        sx={{ ...selectSx, flex: 1 }}
      >
        <MenuItem value="AM" sx={{ fontSize: 12 }}>AM</MenuItem>
        <MenuItem value="PM" sx={{ fontSize: 12 }}>PM</MenuItem>
      </Select>
    </Box>
  );
};

// ── Section header ────────────────────────────────────────────────────────────
const SectionHeader = ({ icon, title }: { icon: React.ReactNode; title: string }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      px: 3,
      py: 2,
      borderBottom: "1px solid",
      borderColor: "divider",
      bgcolor: (t) => t.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "grey.50",
    }}
  >
    <Box
      sx={{
        display: "flex", alignItems: "center", justifyContent: "center",
        width: 34, height: 34, borderRadius: "8px",
        bgcolor: "primary.main", color: "#fff", flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Typography fontWeight={600} fontSize={14}>
      {title}
    </Typography>
  </Box>
);

// ── Main component ────────────────────────────────────────────────────────────
const Index = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const location = useLocation();
  const editData: IRoutes | undefined = (location.state as any)?.routeData;
  const isEditing = !!editData;

  const [createRoute, { isLoading }] = useCreateRouteMutation();
  const { showErrorMessage, showMessage } = useNotifier();

  const [availableCities, setAvailableCities] = useState<string[]>(() => {
    if (editData?.cities) {
      const cities = Array.isArray(editData.cities)
        ? editData.cities
        : JSON.parse(editData.cities as unknown as string);
      return TORONTO_CITIES.filter((c) => !cities.includes(c));
    }
    return TORONTO_CITIES;
  });
  const [selectedCities, setSelectedCities] = useState<string[]>(() => {
    if (editData?.cities) {
      return Array.isArray(editData.cities)
        ? editData.cities
        : JSON.parse(editData.cities as unknown as string);
    }
    return [];
  });
  const [availableSearch, setAvailableSearch] = useState("");
  const [selectedSearch, setSelectedSearch] = useState("");
  const [customCity, setCustomCity] = useState("");
  const [schedule, setSchedule] = useState<DeliverySchedule>(() => {
    if (editData?.delivery_schedule) {
      const raw: any = typeof editData.delivery_schedule === "string"
        ? JSON.parse(editData.delivery_schedule)
        : editData.delivery_schedule;
      const filled: DeliverySchedule = { ...DEFAULT_SCHEDULE };
      DAYS.forEach(({ key }) => {
        const val: string = raw[key] || "";
        if (val) {
          const [start, end] = val.split("-");
          filled[key] = { enabled: true, startTime: start || "", endTime: end || "" };
        }
      });
      return filled;
    }
    return DEFAULT_SCHEDULE;
  });

  // ── City helpers ─────────────────────────────────────────────────────────
  const addCity = (city: string) => {
    setSelectedCities((p) => [...p, city].sort());
    setAvailableCities((p) => p.filter((c) => c !== city));
  };

  const removeCity = (city: string) => {
    setAvailableCities((p) => [...p, city].sort());
    setSelectedCities((p) => p.filter((c) => c !== city));
  };

  const addAll = () => {
    setSelectedCities((p) => [...p, ...availableCities].sort());
    setAvailableCities([]);
  };

  const removeAll = () => {
    setAvailableCities(TORONTO_CITIES);
    setSelectedCities([]);
  };

  const addCustomCity = () => {
    const name = customCity.trim();
    if (!name) return;
    // Avoid duplicates in either list
    if (selectedCities.includes(name) || availableCities.includes(name)) {
      setCustomCity("");
      return;
    }
    setSelectedCities((p) => [...p, name].sort());
    setCustomCity("");
  };

  const filteredAvailable = availableCities.filter((c) =>
    c.toLowerCase().includes(availableSearch.toLowerCase())
  );
  const filteredSelected = selectedCities.filter((c) =>
    c.toLowerCase().includes(selectedSearch.toLowerCase())
  );

  // ── Schedule helpers ──────────────────────────────────────────────────────
  const toggleDay = (day: string) => {
    setSchedule((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        enabled: !prev[day].enabled,
        startTime: prev[day].enabled ? "" : prev[day].startTime,
        endTime:   prev[day].enabled ? "" : prev[day].endTime,
      },
    }));
  };

  const setStartTime = (day: string, v: string) =>
    setSchedule((prev) => ({ ...prev, [day]: { ...prev[day], startTime: v } }));

  const setEndTime = (day: string, v: string) =>
    setSchedule((prev) => ({ ...prev, [day]: { ...prev[day], endTime: v } }));

  const enabledDays = DAYS.filter((d) => schedule[d.key].enabled);

  // ── Formik ────────────────────────────────────────────────────────────────
  const formik = useFormik({
    initialValues: { name: editData?.name || "" },
    onSubmit: async (values, { resetForm }) => {
      // Build schedule for backend — store as "HH:MM-HH:MM" range string
      const deliverySchedule: Record<string, string> = {};
      DAYS.forEach(({ key }) => {
        const s = schedule[key];
        if (s.enabled) deliverySchedule[key] = `${s.startTime}${s.endTime ? `-${s.endTime}` : ""}`;
      });

      try {
        const payload: any = {
          name: values.name,
          cities: selectedCities,
          deliverySchedule,
        };
        if (isEditing) payload.id = editData!.id;

        const res = await createRoute(payload).unwrap();

        if (!res.status) {
          showErrorMessage(res.message);
        } else {
          showMessage(isEditing ? "Route updated successfully" : "Route created successfully");
          resetForm();
          setSelectedCities([]);
          setAvailableCities(TORONTO_CITIES);
          setSchedule(DEFAULT_SCHEDULE);
          navigate("/route/list");
        }
      } catch {
        showErrorMessage("Something went wrong");
      }
    },
  });

  const formValid = useMemo(
    () => formik.values.name.trim() !== "",
    [formik.values.name]
  );

  return (
    <Grid container spacing={3}>
      {/* ── Sidebar ─────────────────────────────────────────────────────── */}
      <Grid item lg={3} md={3} sm={12} xs={12}>
        <Card sx={{ borderRadius: 2, position: "sticky", top: 24 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              {isEditing ? "Edit Route" : "Create Route"}
            </Typography>
            <Typography variant="body2" color="text.secondary" lineHeight={1.7}>
              Define a delivery route with its coverage area and schedule.
            </Typography>

            <Divider sx={{ my: 2 }} />

            {[
              { label: "Route name",    done: formik.values.name.trim() !== "" },
              { label: "Cities added",  done: selectedCities.length > 0 },
              { label: "Schedule set",  done: enabledDays.length > 0 },
            ].map(({ label, done }) => (
              <Box key={label} display="flex" alignItems="center" gap={1} mb={1}>
                <Box sx={{
                  width: 8, height: 8, borderRadius: "50%",
                  bgcolor: done ? "success.main" : "grey.300",
                  transition: "background-color 0.3s",
                }} />
                <Typography variant="body2" color={done ? "success.main" : "text.disabled"}>
                  {label}
                </Typography>
              </Box>
            ))}

            {selectedCities.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  {selectedCities.length} CITIES SELECTED
                </Typography>
                <Box mt={1} display="flex" flexWrap="wrap" gap={0.5}>
                  {selectedCities.slice(0, 8).map((c) => (
                    <Chip key={c} label={c} size="small" variant="outlined" />
                  ))}
                  {selectedCities.length > 8 && (
                    <Chip label={`+${selectedCities.length - 8} more`} size="small" />
                  )}
                </Box>
              </>
            )}

            {enabledDays.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  DELIVERY DAYS
                </Typography>
                <Box mt={1} display="flex" flexWrap="wrap" gap={0.5}>
                  {enabledDays.map(({ key, short }) => {
                    const s = parseTime(schedule[key].startTime);
                    const e = parseTime(schedule[key].endTime);
                    const startLabel = s.h ? `${String(s.h).padStart(2,"0")}:${s.m}${s.period}` : "";
                    const endLabel   = e.h ? `${String(e.h).padStart(2,"0")}:${e.m}${e.period}` : "";
                    const label = startLabel
                      ? `${short} ${startLabel}${endLabel ? `–${endLabel}` : ""}`
                      : short;
                    return (
                      <Chip
                        key={key}
                        label={label}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    );
                  })}
                </Box>
              </>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* ── Form ────────────────────────────────────────────────────────── */}
      <Grid item lg={9} md={9} sm={12} xs={12}>
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={3}>

            {/* ── Route name ──────────────────────────────────────────── */}
            <Grid item xs={12}>
              <Card sx={{ borderRadius: 2 }}>
                <SectionHeader icon={<RouteIcon fontSize="small" />} title="Route Details" />
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="body2" fontWeight={500} mb={0.5} color="text.secondary">
                    Route Name <Box component="span" color="error.main">*</Box>
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="e.g. North York – Scarborough Route"
                    {...formik.getFieldProps("name")}
                    InputProps={{
                      sx: { fontSize: 14, borderRadius: "8px" },
                      startAdornment: (
                        <InputAdornment position="start">
                          <RouteIcon fontSize="small" color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </CardContent>
              </Card>
            </Grid>

            {/* ── City selector ───────────────────────────────────────── */}
            <Grid item xs={12}>
              <Card sx={{ borderRadius: 2 }}>
                <SectionHeader
                  icon={<LocationCityIcon fontSize="small" />}
                  title={`Coverage Area  ${selectedCities.length > 0 ? `— ${selectedCities.length} cities selected` : ""}`}
                />
                <CardContent sx={{ p: 3 }}>

                  {/* ── Add custom city ─────────────────────────────────── */}
                  <Box display="flex" gap={1} mb={2.5} alignItems="center">
                    <TextField
                      size="small"
                      placeholder="Add a custom city not in the list..."
                      value={customCity}
                      onChange={(e) => setCustomCity(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCustomCity())}
                      InputProps={{
                        sx: { fontSize: 13, borderRadius: "8px" },
                        startAdornment: (
                          <InputAdornment position="start">
                            <LocationCityIcon fontSize="small" color="action" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ flex: 1 }}
                    />
                    <Tooltip title="Add custom city">
                      <span>
                        <IconButton
                          color="primary"
                          disabled={!customCity.trim()}
                          onClick={addCustomCity}
                          sx={{
                            border: "1px solid",
                            borderColor: customCity.trim() ? "primary.main" : "divider",
                            borderRadius: "8px",
                          }}
                        >
                          <AddCircleOutlineIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                  <Divider sx={{ mb: 2 }} />

                  <Grid container spacing={2} alignItems="flex-start">

                    {/* Available cities */}
                    <Grid item xs={12} md={5}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="body2" fontWeight={600} color="text.secondary">
                          Available Cities
                        </Typography>
                        <Button
                          size="small"
                          variant="text"
                          onClick={addAll}
                          disabled={availableCities.length === 0}
                          sx={{ fontSize: 11, textTransform: "none" }}
                        >
                          Add All
                        </Button>
                      </Box>
                      <TextField
                        fullWidth size="small" placeholder="Search..."
                        value={availableSearch}
                        onChange={(e) => setAvailableSearch(e.target.value)}
                        InputProps={{
                          sx: { fontSize: 13, borderRadius: "8px" },
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon fontSize="small" color="action" />
                            </InputAdornment>
                          ),
                        }}
                        sx={{ mb: 1 }}
                      />
                      <Box sx={{
                        border: "1px solid", borderColor: "divider",
                        borderRadius: "8px", height: 320, overflowY: "auto",
                      }}>
                        {filteredAvailable.length === 0 ? (
                          <Box p={2} textAlign="center">
                            <Typography variant="body2" color="text.disabled">
                              {availableCities.length === 0 ? "All cities added" : "No match"}
                            </Typography>
                          </Box>
                        ) : (
                          filteredAvailable.map((city) => (
                            <Box
                              key={city}
                              onClick={() => addCity(city)}
                              sx={{
                                display: "flex", justifyContent: "space-between",
                                alignItems: "center", px: 2, py: 1,
                                cursor: "pointer", fontSize: 13,
                                borderBottom: "1px solid", borderColor: "divider",
                                "&:hover": { bgcolor: "primary.50", "& .arrow": { opacity: 1 } },
                              }}
                            >
                              <Typography fontSize={13}>{city}</Typography>
                              <ArrowForwardIcon
                                className="arrow"
                                fontSize="small"
                                sx={{ color: "primary.main", opacity: 0.3, transition: "opacity 0.2s" }}
                              />
                            </Box>
                          ))
                        )}
                      </Box>
                    </Grid>

                    {/* Arrows (desktop) */}
                    <Grid item md={2} sx={{ display: { xs: "none", md: "flex" }, flexDirection: "column", alignItems: "center", justifyContent: "center", pt: 8 }}>
                      <Box display="flex" flexDirection="column" gap={1}>
                        <Button
                          variant="outlined" size="small"
                          onClick={addAll}
                          disabled={availableCities.length === 0}
                          sx={{ minWidth: 36, px: 1, borderRadius: "8px" }}
                          title="Add all"
                        >
                          <ArrowForwardIcon fontSize="small" />
                        </Button>
                        <Button
                          variant="outlined" size="small" color="error"
                          onClick={removeAll}
                          disabled={selectedCities.length === 0}
                          sx={{ minWidth: 36, px: 1, borderRadius: "8px" }}
                          title="Remove all"
                        >
                          <ArrowBackIcon fontSize="small" />
                        </Button>
                      </Box>
                    </Grid>

                    {/* Selected cities */}
                    <Grid item xs={12} md={5}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="body2" fontWeight={600} color="text.secondary">
                          Selected Cities
                          {selectedCities.length > 0 && (
                            <Chip label={selectedCities.length} size="small" color="primary" sx={{ ml: 1, height: 18, fontSize: 11 }} />
                          )}
                        </Typography>
                        <Button
                          size="small" variant="text" color="error"
                          onClick={removeAll}
                          disabled={selectedCities.length === 0}
                          sx={{ fontSize: 11, textTransform: "none" }}
                        >
                          Clear All
                        </Button>
                      </Box>
                      <TextField
                        fullWidth size="small" placeholder="Search..."
                        value={selectedSearch}
                        onChange={(e) => setSelectedSearch(e.target.value)}
                        InputProps={{
                          sx: { fontSize: 13, borderRadius: "8px" },
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon fontSize="small" color="action" />
                            </InputAdornment>
                          ),
                        }}
                        sx={{ mb: 1 }}
                      />
                      <Box sx={{
                        border: "1px solid", borderColor: "divider",
                        borderRadius: "8px", height: 320, overflowY: "auto",
                      }}>
                        {filteredSelected.length === 0 ? (
                          <Box p={2} textAlign="center">
                            <Typography variant="body2" color="text.disabled">
                              {selectedCities.length === 0
                                ? "Click cities on the left to add"
                                : "No match"}
                            </Typography>
                          </Box>
                        ) : (
                          filteredSelected.map((city) => (
                            <Box
                              key={city}
                              onClick={() => removeCity(city)}
                              sx={{
                                display: "flex", justifyContent: "space-between",
                                alignItems: "center", px: 2, py: 1,
                                cursor: "pointer", fontSize: 13,
                                borderBottom: "1px solid", borderColor: "divider",
                                "&:hover": { bgcolor: "error.50", "& .back": { opacity: 1 } },
                              }}
                            >
                              <Box display="flex" alignItems="center" gap={1}>
                                <CheckCircleIcon sx={{ fontSize: 14, color: "success.main" }} />
                                <Typography fontSize={13}>{city}</Typography>
                              </Box>
                              <ArrowBackIcon
                                className="back"
                                fontSize="small"
                                sx={{ color: "error.main", opacity: 0.3, transition: "opacity 0.2s" }}
                              />
                            </Box>
                          ))
                        )}
                      </Box>
                    </Grid>

                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* ── Delivery schedule ────────────────────────────────────── */}
            <Grid item xs={12}>
              <Card sx={{ borderRadius: 2 }}>
                <SectionHeader
                  icon={<CalendarMonthIcon fontSize="small" />}
                  title={`Delivery Schedule${enabledDays.length > 0 ? `  — ${enabledDays.length} day${enabledDays.length > 1 ? "s" : ""} active` : ""}`}
                />
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="body2" color="text.secondary" mb={2.5}>
                    Toggle the days this route is active and set the delivery time for each.
                  </Typography>

                  <Box
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: "10px",
                      overflow: "hidden",
                    }}
                  >
                    {DAYS.map(({ key, short, weekend }, idx) => {
                      const day = schedule[key];
                      const startParsed = parseTime(day.startTime);
                      const endParsed   = parseTime(day.endTime);
                      const startLabel  = startParsed.h ? `${String(startParsed.h).padStart(2,"0")}:${startParsed.m} ${startParsed.period}` : "";
                      const endLabel    = endParsed.h   ? `${String(endParsed.h).padStart(2,"0")}:${endParsed.m} ${endParsed.period}`     : "";
                      const rangeLabel  = startLabel && endLabel ? `${startLabel} – ${endLabel}` : startLabel || "";

                      return (
                        <Box
                          key={key}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            px: 2,
                            py: 1.5,
                            borderBottom: idx < DAYS.length - 1 ? "1px solid" : "none",
                            borderColor: "divider",
                            transition: "background-color 0.2s",
                            bgcolor: day.enabled
                              ? (t) => t.palette.mode === "dark" ? "rgba(25,118,210,0.08)" : "#f0f7ff"
                              : "transparent",
                          }}
                        >
                          {/* Toggle */}
                          <Switch
                            size="small"
                            checked={day.enabled}
                            onChange={() => toggleDay(key)}
                            color={weekend ? "error" : "primary"}
                          />

                          {/* Day name */}
                          <Box sx={{ width: 95, flexShrink: 0 }}>
                            <Typography
                              fontWeight={day.enabled ? 700 : 400}
                              fontSize={13}
                              color={weekend ? "error.main" : day.enabled ? "text.primary" : "text.disabled"}
                            >
                              {key}
                            </Typography>
                            <Typography fontSize={11} color="text.disabled">{short}</Typography>
                          </Box>

                          {/* From / To pickers */}
                          <Box
                            sx={{
                              flex: 1,
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                              opacity: day.enabled ? 1 : 0.35,
                              pointerEvents: day.enabled ? "auto" : "none",
                            }}
                          >
                            {/* From */}
                            <Box sx={{ flex: 1 }}>
                              <Typography fontSize={10} color="text.secondary" fontWeight={600} mb={0.3}>
                                FROM
                              </Typography>
                              <QuarterTimePicker
                                value={day.startTime}
                                onChange={(v) => setStartTime(key, v)}
                                disabled={!day.enabled}
                              />
                            </Box>

                            {/* Separator */}
                            <Typography
                              fontSize={16}
                              color="text.disabled"
                              sx={{ mt: 2.5, flexShrink: 0 }}
                            >
                              →
                            </Typography>

                            {/* To */}
                            <Box sx={{ flex: 1 }}>
                              <Typography fontSize={10} color="text.secondary" fontWeight={600} mb={0.3}>
                                TO
                              </Typography>
                              <QuarterTimePicker
                                value={day.endTime}
                                onChange={(v) => setEndTime(key, v)}
                                disabled={!day.enabled}
                              />
                            </Box>
                          </Box>

                          {/* Range badge */}
                          <Box sx={{ width: 150, flexShrink: 0, textAlign: "right" }}>
                            {day.enabled && rangeLabel ? (
                              <Chip
                                label={rangeLabel}
                                size="small"
                                color={weekend ? "error" : "primary"}
                                variant="filled"
                                sx={{ fontSize: 11, height: 22, maxWidth: "100%" }}
                              />
                            ) : (
                              <Typography fontSize={11} color="text.disabled">
                                {day.enabled ? "Set time range" : "—"}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>

                  {/* Weekday / Weekend quick toggles */}
                  <Box display="flex" gap={1} mt={2.5} flexWrap="wrap">
                    <Button
                      size="small" variant="outlined"
                      sx={{ fontSize: 12, textTransform: "none", borderRadius: "8px" }}
                      onClick={() => {
                        setSchedule((prev) => {
                          const next = { ...prev };
                          DAYS.filter((d) => !d.weekend).forEach((d) => {
                            next[d.key] = { ...next[d.key], enabled: true };
                          });
                          return next;
                        });
                      }}
                    >
                      Enable Weekdays
                    </Button>
                    <Button
                      size="small" variant="outlined" color="error"
                      sx={{ fontSize: 12, textTransform: "none", borderRadius: "8px" }}
                      onClick={() => {
                        setSchedule((prev) => {
                          const next = { ...prev };
                          DAYS.filter((d) => d.weekend).forEach((d) => {
                            next[d.key] = { ...next[d.key], enabled: true };
                          });
                          return next;
                        });
                      }}
                    >
                      Enable Weekend
                    </Button>
                    <Button
                      size="small" variant="outlined"
                      sx={{ fontSize: 12, textTransform: "none", borderRadius: "8px" }}
                      onClick={() => {
                        setSchedule((prev) => {
                          const next = { ...prev };
                          DAYS.forEach((d) => { next[d.key] = { ...next[d.key], enabled: true }; });
                          return next;
                        });
                      }}
                    >
                      Enable All
                    </Button>
                    <Button
                      size="small" variant="text"
                      sx={{ fontSize: 12, textTransform: "none", borderRadius: "8px" }}
                      onClick={() => setSchedule(DEFAULT_SCHEDULE)}
                    >
                      Clear All
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* ── Actions ─────────────────────────────────────────────── */}
            <Grid item xs={12}>
              <Card sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Box display="flex" justifyContent="flex-end" gap={1.5}>
                    <Button
                      variant="outlined"
                      startIcon={<CloseIcon />}
                      onClick={() => {
                        formik.resetForm();
                        setSelectedCities([]);
                        setAvailableCities(TORONTO_CITIES);
                        setSchedule(DEFAULT_SCHEDULE);
                      }}
                      sx={{ borderRadius: "8px", textTransform: "none", px: 3 }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <SaveAltIcon />}
                      onClick={() => formik.handleSubmit()}
                      disabled={!formValid || isLoading}
                      sx={{ borderRadius: "8px", textTransform: "none", px: 3 }}
                    >
                      {isLoading ? "Saving..." : isEditing ? "Update Route" : "Save Route"}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

          </Grid>
        </form>
      </Grid>
    </Grid>
  );
};

export default Index;
